/**
 * Values of the "in list" / "not in list" filter operators as the list entry
 * (`FilterValueListInput`) builds them: one value per Enter, or several from
 * one paste. Each value is typed like the single-value input would type it for
 * the column (`FilterValueInput`, both `numberFieldValue`): a number column's
 * entries are numbers (the emitters write them unquoted), every other column's
 * are text. A number a JS number cannot hold exactly (`9007199254740993`)
 * keeps its text in plain decimal, which the emitters write as a bare number
 * on a numeric column (`sqlNumericLiteral.ts`).
 */
import type { DataTypeCategory } from './filterUtils';
import { numberFieldValue } from './sqlNumericLiteral';

/** One value of a list filter — the element type of `FilterCondition['value']`'s list. */
export type ListValue = string | number;

/**
 * The list value of what the entry field holds, or `null` when it holds
 * nothing (empty or only spaces) — or, on a number column, a number spelled
 * past the digit limit (refused, `numberFieldValue`). A number column's
 * numeric text becomes a number when a JS number holds it exactly, else its
 * plain decimal text; any other text stays text, as the single-value input
 * keeps it.
 */
export function toListValue(entry: unknown, category: DataTypeCategory): ListValue | null {
  if (typeof entry === 'number') return Number.isFinite(entry) ? entry : null;
  if (entry instanceof Date) return Number.isNaN(entry.getTime()) ? null : entry.toISOString();
  if (typeof entry !== 'string' || entry.trim() === '') return null;
  return category === 'number' ? numberFieldValue(entry) : entry;
}

/** Separators between pasted values: line breaks, tabs (spreadsheet cells) and commas. */
const PASTE_SEPARATOR_RE = /\r\n|[\r\n\t,]/;

/**
 * The values in pasted text — one per line, or separated by tabs or commas,
 * each trimmed — and the pieces `toListValue` refuses (a number spelled past
 * the digit limit), or `null` when the text is one value, which the entry
 * field takes as typed. A date column always takes the paste here: its date
 * picker cannot take pasted text.
 */
export function parseListEntries(
  text: string,
  category: DataTypeCategory,
): { values: ListValue[]; refused: string[] } | null {
  const pieces = text.split(PASTE_SEPARATOR_RE).map((p) => p.trim()).filter((p) => p !== '');
  if (pieces.length === 0) return null;
  if (pieces.length === 1 && category !== 'datetime') return null;
  const values: ListValue[] = [];
  const refused: string[] = [];
  for (const piece of pieces) {
    const value = toListValue(piece, category);
    if (value === null) refused.push(piece);
    else values.push(value);
  }
  return { values, refused };
}

/** `values` without repeats (first occurrence kept; `1` and `'1'` differ). */
export function uniqueListValues(values: readonly ListValue[]): ListValue[] {
  const seen = new Set<string>();
  return values.filter((v) => {
    const key = `${typeof v}:${String(v)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
